import React, { ChangeEvent, useState, useEffect, useCallback, useContext } from "react";
// ... (all original imports) ...

// Import custom hooks (adjust paths based on your project structure)
import useBulkOrderCsvLogic from "@hooks/orders/useBulkOrderCsvLogic";
import useBulkOrderSubmitProcess from "@hooks/orders/useBulkOrderSubmitProcess";
import useShipstationSelection from "@hooks/orders/useShipstationSelection";
import useBulkOrderSockets from "@hooks/orders/useBulkOrderSockets";
import OwButton from "@components/shared/OwButton/OwButton";
import OwCheckbox from "@components/shared/OwCheckbox/OwCheckbox";
import OwCsvReader from "@components/shared/OwCsvReader/OwCsvReader";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwProgressBar from "@components/shared/OwProgressBar/OwProgressBar";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import OwWeightSelector from "@components/shared/OwWeightSelector/OwWeightSelector";
import IErrorDTO from "@models/DTO/IErrorDTO";
import IOnValidateBulkOrderSubmitDTO from "@models/DTO/IOnValidateBulkOrderSubmitDTO";
import IValidateBulkOrderSubmitDTO from "@models/DTO/IValidateBulkOrderSubmitDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import INotification from "@models/INotification";
import IOrder_Batch from "@models/IOrder_Batch";
import ISupplierModel from "@models/ISupplierModel";
import IUserUI from "@models/IUserUI";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import { Socket } from "dgram";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import { SocketContext } from "@contexts/SocketContext";
import UiContext from "@contexts/UiContext";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";

// Define the props using the existing type alias
type Props = {
    orderBatch?: IOrder_Batch;
    orderGuids: string[];
    closeModalFn: (refreshData?: boolean) => void;
    selectAll: boolean; // Still unused, but keeping for type completeness
};

export const BulkOrderSubmitFnModal: React.FC<Props> = ({ orderBatch, orderGuids, closeModalFn, selectAll }) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    // State managed directly by the main component
    const [shippingOrders, setShippingOrders] = useState<{ guid: string; name: string }[]>([]);
    const [hasValidatedGuids, setHasValidatedGuids] = useState(false);
    const [currentTotal, setCurrentTotal] = useState(orderGuids.length); // Total based on initial props

    // Use custom hooks and destructure their returned values
    const {
        isProcessing: isCsvProcessing, // Rename to avoid conflict if another hook has isProcessing
        fileDropRejected,
        fileTypeRejected,
        file,
        csvData,
        csvLoaded,
        onFileDropRejected, // Pass these down to OwCsvReader
        onFileTypeRejected,
        onFileLoadStart,
        onFileLoadEnd,
    } = useBulkOrderCsvLogic();

    const {
        shipstationCarriers,
        shipstationServiceTypes,
        shipstationPackageTypes,
        shipstationCarrier,
        setShipstationCarrier, // Need setters for handleChange
        shipstationServiceType,
        setShipstationServiceType, // Need setters for handleChange
        shipstationPackageType,
        setShipstationPackageType, // Need setters for handleChange
        activeGetCarriers,
        activeGetServiceTypes,
        activeGetPackageTypes,
        // Handlers needed by useSocketListeners
        onGetShipstationCarriers,
        onGetShipstationServicesForCarrier,
        onGetShipstationPackageTypesForCarrier,
    } = useShipstationSelection({
        socket,
        userModel,
        supplierModel,
        hasValidatedGuids, // Dependency for initial carrier fetch
        shippingOrdersLength: shippingOrders.length, // Dependency for initial carrier fetch
    });

    const {
        submitActive,
        setSubmitActive,
        totalProcessed,
        totalErrors,
        errorDetails,
        currentProgress,
        // currentTotal, // Now managed here
        progress,
        completed,
        shipmentBatchNo,
        shipmentBatchToken,
        autoCreatePackages,
        setAutoCreatePackages, // Need setters for handleChange
        advanceToShipped,
        setAdvanceToShipped, // Need setters for handleChange
        updateShipstationOrderAddress,
        setUpdateShipstationOrderAddress, // Need setters for handleChange
        shipstation_bulk_update_accept,
        setShipstationBulkUpdateAccept, // Need setters for handleChange
        defaultWeight,
        setDefaultWeight, // Need setters for handleChange
        submitBulkOrders, // Need the submit function
        getShipmentBatchUrl, // Need helper function
        downloadErrorCsv, // Need helper function
        // Handlers needed by useSocketListeners
        onBulkOrderSubmit,
        bulkOrderSubmitStart,
        bulkOrderSubmitProgress,
        hasShippingIntegration,
    } = useBulkOrderSubmitProcess({
        socket,
        userModel,
        supplierModel,
        orderGuids,
        orderBatch,
        csvData, // Pass data from useCsvReaderLogic
        // Pass Shipstation options if uncommented in payload
        // shipstationCarrier,
        // shipstationServiceType,
        // shipstationPackageType,
        currentTotal, // Pass currentTotal from the main component
    });

    // Define the socket event handler for validation response (updates main component state)
    const onValidateBulkOrderSubmit = useCallback(
        (data: IOnValidateBulkOrderSubmitDTO) => {
            const notification: INotification = {
                message: "successfully_validated_orders",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };
            uiContext.notifyFn(null, notification); // Use prop
            setShippingOrders(data.shippingOrders); // Update main component state
            setHasValidatedGuids(true); // Update main component state
            // setCurrentTotal(orderGuids.length); // Update total based on validated orders
        },
        [uiContext.notifyFn]
    ); // Dependency: notifyFn prop is a dependency of this handler

    const notifyFn = (notification: INotification) => {
        console.log("notifyFn", notification);
        setSubmitActive(false);
    };

    // Set up socket listeners using the custom hook, passing all necessary handlers
    useBulkOrderSockets({
        socket,
        supplierModel,
        handlers: {
            onBulkOrderSubmit, // Handler from useBulkSubmissionProcess
            bulkOrderSubmitStart, // Handler from useBulkSubmissionProcess
            bulkOrderSubmitProgress, // Handler from useBulkSubmissionProcess
            onValidateBulkOrderSubmit, // Handler from main component
            onGetShipstationCarriers, // Handler from useShipstationSelection
            onGetShipstationServicesForCarrier, // Handler from useShipstationSelection
            onGetShipstationPackageTypesForCarrier, // Handler from useShipstationSelection
            notifyFn,
        },
    });

    // Initial validation fetch on mount - stays in main component as it's tied to core logic
    const validateBulkOrderSubmit = useCallback(() => {
        const payload: IValidateBulkOrderSubmitDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuids: orderGuids,
        };
        socket.emit(EndpointsEnum.OrderEntryValidateBulkOrderSubmit, payload);
    }, [userModel, supplierModel, orderGuids, socket]); // Dependencies

    // Effect to call initial validation on mount (similar to componentDidMount)
    useEffect(() => {
        validateBulkOrderSubmit();
    }, [validateBulkOrderSubmit]); // Dependency: validateBulkOrderSubmit function (wrapped in useCallback)

    // Generic change handler to distribute updates to appropriate setters from hooks or component state
    const handleChange = useCallback(
        (e: ChangeEvent<any>) => {
            const { name, value, type, checked } = e.target;
            const newValue = type === "checkbox" ? checked : value;

            // Route the state update to the correct setter based on the input name
            if (name === "autoCreatePackages") {
                setAutoCreatePackages(newValue); // Setter from useBulkSubmissionProcess
            } else if (name === "updateShipstationOrderAddress") {
                setUpdateShipstationOrderAddress(newValue); // Setter from useBulkSubmissionProcess
            } else if (name === "shipstation_bulk_update_accept") {
                setShipstationBulkUpdateAccept(newValue); // Setter from useBulkSubmissionProcess
            } else if (name === "shipstationCarrier") {
                setShipstationCarrier(newValue); // Setter from useShipstationSelection
            } else if (name === "shipstationServiceType") {
                setShipstationServiceType(newValue); // Setter from useShipstationSelection
            } else if (name === "shipstationPackageType") {
                setShipstationPackageType(newValue); // Setter from useShipstationSelection
            } else if (name === "defaultWeight") {
                setDefaultWeight(newValue); // Setter from useBulkSubmissionProcess
            } else if (name === "advanceToShipped") {
                setAdvanceToShipped(newValue); // Setter from useBulkSubmissionProcess
            }
            // Add other input names and their corresponding setters if necessary
        },
        [
            // Dependencies are the setters from the hooks/main component that this handler calls
            setAutoCreatePackages,
            setUpdateShipstationOrderAddress,
            setShipstationBulkUpdateAccept,
            setShipstationCarrier,
            setShipstationServiceType,
            setShipstationPackageType,
            setDefaultWeight,
        ]
    );

    // Helper to download the initial ship details CSV (uses main component state)
    const downloadShipDetailsCSV = useCallback(() => {
        const headerRow = ["Order", "Weight", "Width", "Height", "Length", "Guid"];
        const rows = [headerRow];
        // Use state from main component
        for (const order of shippingOrders) {
            rows.push([order.name, "", "", "", "", order.guid]);
        }
        const csvContent = "data:text/csv;charset=utf-8," + rows.map((e) => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "orderwerks-bulk-ship-details.csv");
        link.click();
    }, [shippingOrders]); // Dependency: shippingOrders state from main component

    // Validation logic using state from different hooks and main component state/props
    const isFormValid = useCallback(() => {
        let isValid = true;

        // The original class commented this Shipstation validation out
        // if (supplierModel.Supplier_Integration.has_shipstation && shippingOrders.length && autoCreatePackages) {
        //     if (
        //         !shipstationCarrier.length ||
        //         !shipstationPackageType.length ||
        //         !shipstationServiceType.length
        //     ) {
        //         isValid = false;
        //     }
        // }

        if (shippingOrders.length) {
            // Check for CSV data if autoCreatePackages is true and no default weight is configured
            if (autoCreatePackages && !supplierModel.bulk_shipments_default_weight && !csvData) {
                isValid = false;
            }

            // Check Shipstation update confirmation
            if (updateShipstationOrderAddress && shipstation_bulk_update_accept !== "YES") {
                isValid = false;
            }
        }

        return isValid;
    }, [
        autoCreatePackages, // State from useBulkSubmissionProcess
        supplierModel.bulk_shipments_default_weight, // Prop
        csvData, // State from useCsvReaderLogic
        updateShipstationOrderAddress, // State from useBulkSubmissionProcess
        shipstation_bulk_update_accept, // State from useBulkSubmissionProcess
        // Include Shipstation states if validation uncommented:
        // shipstationCarrier, shipstationServiceType, shipstationPackageType, // State from useShipstationSelection
        // shippingOrders.length, // State from main component
        // supplierModel.Supplier_Integration.has_shipstation // Prop
    ]); // Dependencies

    const closeModal = useCallback(() => {
        closeModalFn(true); // Use prop
    }, [closeModalFn]); // Dependency: closeModalFn prop

    // --- Render Logic ---
    return (
        <div className="BulkOrderSubmitModal fnModal p-2">
            <div className={"w-100 mb-3"}>
                <div className={"d-flex justify-content-between"}>
                    <OwTitle leftIconName={"fad fa-cloud-upload-alt"} title={"bulk_order_submit"} tag={"h3"} />
                    <div>
                        {/* Use function from main component */}
                        <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                    </div>
                </div>
            </div>

            {/* Use state from main component */}
            {hasValidatedGuids ? null : (
                <div className={"mb-3"}>
                    <OwLabel title={"validating_orders"} leftIconClass={"fad fa-sync fa-spin"} noBottomMargin={true} />
                </div>
            )}

            {/* Use state from main component and useBulkSubmissionProcess */}
            {shippingOrders.length > 0 && !completed && (
                <div>
                    <div className={"mb-3"}>
                        {/* Use state and setter from useBulkSubmissionProcess via handleChange */}
                        <div>
                            <OwToggle
                                name="autoCreatePackages"
                                labelOnRight={true}
                                label="auto_create_shipments"
                                active={autoCreatePackages}
                                fn={handleChange}
                                disabled={!hasShippingIntegration}
                            />
                        </div>
                        {!autoCreatePackages && (
                            <div className="d-flex align-items-center">
                                <OwToggle
                                    name="advanceToShipped"
                                    labelOnRight={true}
                                    label="advance_to_shipped"
                                    active={advanceToShipped}
                                    fn={handleChange}
                                />
                                <OwIconTooltip
                                    iconClassName="ms-2 fas fa-info-circle"
                                    tooltipContent="advance_to_shipped_tooltip"
                                    translateContent={true}
                                />
                            </div>
                        )}
                    </div>

                    {/* Use prop */}
                    {supplierModel.Supplier_Integration.has_shipstation && (
                        <div className={"mb-3"}>
                            {/* Use state and setter from useBulkSubmissionProcess via handleChange */}
                            <OwToggle
                                name="updateShipstationOrderAddress"
                                labelOnRight={true}
                                label="update_shipstation_order_ship_to"
                                active={updateShipstationOrderAddress}
                                fn={handleChange}
                            />
                            {/* Use state from useBulkSubmissionProcess */}
                            {updateShipstationOrderAddress ? (
                                <div>
                                    <div className={"d-flex w-40r text-wrap"}>
                                        <OwLabel
                                            title={"bulk_update_shipstation_order_warning"}
                                            className={"text-warning"}
                                            leftIconClass={"fad fa-exclamation-triangle"}
                                        />
                                    </div>
                                    <div className={"col-2 .g-0"}>
                                        {/* Use state and setter from useBulkSubmissionProcess via handleChange */}
                                        <OwInputWrapper
                                            name={"shipstation_bulk_update_accept"}
                                            fn={handleChange}
                                            value={shipstation_bulk_update_accept}
                                        />
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Use prop and state from useBulkSubmissionProcess */}
                    {supplierModel.Supplier_Integration.has_shipstation && autoCreatePackages && (
                        <div className="border p-2">
                            <OwLabel title="shipstation_shipment_detail_override" className="mb-2" />
                            <div className={"mb-3"}>
                                {supplierModel.bulk_shipments_default_weight ? (
                                    <div>
                                        <OwLabel
                                            title={"using_default_bulk_weight"}
                                            postText={":"}
                                            noBottomMargin={true}
                                            className={"me-2"}
                                        />
                                        <OwWeightSelector
                                            onChange={(e: any) => {}}
                                            name={"default"}
                                            disabled={true}
                                            weight={Number(supplierModel.bulk_shipments_default_weight || 0)}
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        {/* Use function from main component */}
                                        <OwButton title={"download_ship_details_csv"} fn={downloadShipDetailsCSV} />

                                        <div className={"mt-3"}>
                                            <div className={"drop-area"}>
                                                {/* Use function and state from useCsvReaderLogic */}
                                                <OwCsvReader onFileLoaded={csvLoaded} isProcessing={isCsvProcessing} />
                                            </div>

                                            <div className={"mt-2"}>
                                                {/* Use state from useCsvReaderLogic */}
                                                {file ? (
                                                    <div>
                                                        <OwLabel
                                                            title={"loaded_file"}
                                                            noBottomMargin={true}
                                                            isBold={true}
                                                            isUnderline={true}
                                                        />
                                                        : {file.name}
                                                    </div>
                                                ) : null}
                                                <div className={"d-flex justify-content-between error-section"}>
                                                    {/* Use state from useCsvReaderLogic */}
                                                    <OwLabel
                                                        className={"text-danger mt-2 " + (fileDropRejected ? "" : "d-none")}
                                                        title={"file_drop_rejected_too_big"}
                                                    />
                                                    {/* Use state from useCsvReaderLogic */}
                                                    <OwLabel
                                                        className={"text-danger mt-2 " + (fileTypeRejected ? "" : "d-none")}
                                                        title={"file_type_invalid"}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Use state from main component */}
            {hasValidatedGuids ? (
                <div>
                    <div className={"w-100 mb-3"}>
                        {/* Use state from useBulkSubmissionProcess and main component */}
                        <OwProgressBar
                            title={"progress"}
                            showTitle={true}
                            showTitleChunk={true}
                            currentPercent={progress}
                            titleChunkCurrent={currentProgress}
                            titleChunkTotal={currentTotal} // Use state from main component
                        />
                    </div>

                    {/* Use state from useBulkSubmissionProcess */}
                    {completed ? (
                        <div>
                            <OwLabel title={"results"} noBottomMargin={true} />
                            <div className={"mb-3"}>
                                <div>
                                    <OwLabel title={"processed"} isBold={true} postText={":"} className={"me-1"} noBottomMargin={true} />
                                    {totalProcessed} {/* Use state from useBulkSubmissionProcess */}
                                </div>
                            </div>
                            {/* Use state from useBulkSubmissionProcess */}
                            {totalErrors ? (
                                <div className={"mb-3"}>
                                    <div>
                                        <OwLabel title={"errors"} isBold={true} postText={":"} className={"me-1"} noBottomMargin={true} />
                                        {totalErrors} {/* Use state from useBulkSubmissionProcess */}
                                    </div>
                                    <div className={"mt-2 mb-2"}>
                                        {/* Use function from useBulkSubmissionProcess */}
                                        <OwLabel
                                            leftIconClass={"fad fa-download"}
                                            title={"download_error_csv"}
                                            noBottomMargin={true}
                                            fn={downloadErrorCsv}
                                        />
                                    </div>
                                    <div>
                                        {/* Map over state from useBulkSubmissionProcess */}
                                        {errorDetails?.map((detail, index) => {
                                            return (
                                                <div key={index} className={"error-detail-message"}>
                                                    {detail.message}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : null}
                            <div>
                                {/* Use state from useBulkSubmissionProcess */}
                                {shipmentBatchNo ? (
                                    <div>
                                        <div className={"mb-3"}>
                                            {/* Use state from useBulkSubmissionProcess */}
                                            <OwLabel title={"shipment_batch_no"} postText={":"} className={"me-2"} /> {shipmentBatchNo}
                                        </div>
                                        <div>
                                            <OwLabel title={"view_batch_pdf"} />
                                        </div>
                                        <div className={"mb-2"}>
                                            {/* Use function from useBulkSubmissionProcess */}
                                            <a href={getShipmentBatchUrl()} target={"_blank"} rel="noreferrer">
                                                <OwLabel title={"with_packing_lists"} />
                                            </a>
                                        </div>
                                        <div>
                                            {/* Use function from useBulkSubmissionProcess */}
                                            <a href={getShipmentBatchUrl(true)} target={"_blank"} rel="noreferrer">
                                                <OwLabel title={"without_packing_lists"} />
                                            </a>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    <div>
                        {/* Use state/functions from useBulkSubmissionProcess and main component */}
                        <OwButton
                            title={completed ? "close" : "start"}
                            fn={() => {
                                if (!completed) {
                                    submitBulkOrders(); // Function from useBulkSubmissionProcess
                                } else {
                                    closeModal(); // Function from main component
                                }
                            }}
                            disabled={!isFormValid()} // Function from main component
                            active={submitActive} // State from useBulkSubmissionProcess
                            disableOnActive={true}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
};
